import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../utils/api';
import { useMode } from '../contexts/ModeContext';
import { ArrowLeft, Volume2, CheckCircle2 } from 'lucide-react';

function LetterDetail() {
  const { letterId } = useParams();
  const navigate = useNavigate();
  const { isKidsMode } = useMode();
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchLetterDetail();
  }, [letterId]);

  const fetchLetterDetail = async () => {
    try {
      const response = await apiClient.get(`/elifba/letters/${letterId}`);
      setLetter(response.data.letter);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching letter:', error);
      toast.error('Harf yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const playSound = (audioPath, soundLabel) => {
    // Web Speech API kullanarak basit TTS fallback'i — gerçek ses dosyaları eklendiğinde kullanılacak
    if (audioPath) {
      try {
        const audio = new Audio(audioPath);
        audio.play().catch(() => {
          toast(`🔊 ${soundLabel} sesi yakında eklenecek!`, { duration: 1500 });
        });
      } catch {
        toast(`🔊 ${soundLabel} sesi yakında eklenecek!`, { duration: 1500 });
      }
    } else {
      toast(`🔊 ${soundLabel} sesi yakında eklenecek!`, { duration: 1500 });
    }
  };

  const markAsCompleted = async () => {
    try {
      setCompleting(true);
      await apiClient.post(`/elifba/complete-letter/${letterId}`);
      toast.success('Tamamlandı!', { duration: 1500 });
      setTimeout(() => navigate('/elifba'), 1200);
    } catch (error) {
      console.error('Error marking letter as completed:', error);
      const status = error?.response?.status;
      if (status === 401) {
        toast.error('Giriş yapmanız gerekiyor');
        navigate('/auth');
      } else {
        toast.error('Hata oluştu, tekrar deneyin');
      }
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center" data-testid="letter-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center" data-testid="letter-not-found">
        <div className="text-center">
          <div className="text-2xl text-gray-600 mb-4">Harf bulunamadı</div>
          <button
            onClick={() => navigate('/elifba')}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg"
            data-testid="letter-back-button"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  const harekeColors = isKidsMode
    ? {
        fatha: 'from-rose-100 to-rose-200 text-rose-900',
        kasra: 'from-sky-100 to-sky-200 text-sky-900',
        damma: 'from-emerald-100 to-emerald-200 text-emerald-900',
        fathaBtn: 'bg-rose-500 hover:bg-rose-600',
        kasraBtn: 'bg-sky-500 hover:bg-sky-600',
        dammaBtn: 'bg-emerald-500 hover:bg-emerald-600',
      }
    : {
        fatha: 'from-stone-100 to-stone-200 text-stone-800',
        kasra: 'from-stone-100 to-stone-200 text-stone-800',
        damma: 'from-stone-100 to-stone-200 text-stone-800',
        fathaBtn: 'bg-emerald-700 hover:bg-emerald-800',
        kasraBtn: 'bg-amber-700 hover:bg-amber-800',
        dammaBtn: 'bg-teal-700 hover:bg-teal-800',
      };

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'linear-gradient(135deg, #FEF3C7 0%, #DBEAFE 50%, #E9D5FF 100%)'
          : 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)'
      }}
      data-testid="letter-detail-page"
    >
      {/* Header */}
      <header className="px-6 py-4 bg-white/60 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/elifba')}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
            data-testid="letter-back-to-list"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Harfler</span>
          </button>
          <h1 className={`text-2xl font-bold ${isKidsMode ? 'font-kids text-green-600' : 'font-elegant text-emerald-900'}`} data-testid="letter-title">
            {letter.name} Harfi
          </h1>
          <div className="w-24"></div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Big Letter Card */}
        <div
          className={`p-12 mb-8 ${
            isKidsMode
              ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_8px_0_0_rgba(0,0,0,0.08)]'
              : 'bg-white/80 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm'
          }`}
          data-testid="letter-big-card"
        >
          <div className="text-center">
            <div className={`text-9xl font-arabic mb-4 ${
              isKidsMode
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-blue-500'
                : 'text-emerald-900'
            }`}>
              {letter.letter}
            </div>
            <div className={`text-3xl font-bold ${isKidsMode ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'}`}>
              {letter.name}
            </div>
            {letter.name_ar && (
              <div className="text-xl text-gray-600 font-arabic mt-2">
                {letter.name_ar}
              </div>
            )}
          </div>
        </div>

        {/* Sounds Section */}
        {letter.sounds && (
          <div
            className={`p-8 mb-8 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_6px_0_0_rgba(0,0,0,0.08)]'
                : 'bg-white/80 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm'
            }`}
            data-testid="letter-sounds-section"
          >
            <h2 className={`text-2xl font-bold mb-6 text-center ${isKidsMode ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'}`}>
              Sesler (Harekeler)
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { key: 'fatha', label: 'Üstün (Fatha)', bg: harekeColors.fatha, btn: harekeColors.fathaBtn },
                { key: 'kasra', label: 'Esre (Kasra)', bg: harekeColors.kasra, btn: harekeColors.kasraBtn },
                { key: 'damma', label: 'Ötre (Damma)', bg: harekeColors.damma, btn: harekeColors.dammaBtn },
              ].map(({ key, label, bg, btn }) => (
                letter.sounds[key] && (
                  <div key={key} className={`bg-gradient-to-br ${bg} rounded-2xl p-6`} data-testid={`sound-card-${key}`}>
                    <div className="text-center mb-4">
                      <div className="text-6xl font-arabic mb-2">{letter.sounds[key].example}</div>
                      <div className="text-lg font-semibold">{label}</div>
                      <div className="text-sm opacity-80">{letter.sounds[key].sound}</div>
                    </div>
                    <button
                      onClick={() => playSound(letter.sounds[key].audio, label)}
                      className={`w-full ${btn} text-white py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2`}
                      data-testid={`play-sound-${key}`}
                    >
                      <Volume2 className="w-4 h-4" />
                      Dinle
                    </button>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* Examples Section */}
        {letter.examples && letter.examples.length > 0 && (
          <div
            className={`p-8 mb-8 ${
              isKidsMode
                ? 'bg-white rounded-3xl border-2 border-slate-100 shadow-[0_6px_0_0_rgba(0,0,0,0.08)]'
                : 'bg-white/80 backdrop-blur-sm rounded-xl border border-stone-200 shadow-sm'
            }`}
            data-testid="letter-examples-section"
          >
            <h2 className={`text-2xl font-bold mb-6 text-center ${isKidsMode ? 'font-kids text-slate-800' : 'font-elegant text-stone-900'}`}>
              Örnek Kelimeler
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {letter.examples.map((example, index) => (
                <div
                  key={index}
                  className={`text-center p-6 ${
                    isKidsMode
                      ? 'bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl'
                      : 'bg-stone-50 rounded-xl border border-stone-200'
                  }`}
                  data-testid={`example-${index}`}
                >
                  <div className="text-3xl font-arabic text-gray-800">{example}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Complete Button */}
        <div className="flex justify-center">
          <button
            onClick={markAsCompleted}
            disabled={completing}
            className={`px-12 py-4 text-white text-xl font-bold transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 ${
              isKidsMode
                ? 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 rounded-2xl shadow-[0_6px_0_0_rgba(0,0,0,0.15)] hover:shadow-[0_3px_0_0_rgba(0,0,0,0.15)] hover:translate-y-0.5'
                : 'bg-emerald-700 hover:bg-emerald-800 rounded-xl shadow-sm'
            }`}
            data-testid="complete-letter-button"
          >
            <CheckCircle2 className="w-6 h-6" />
            {completing ? 'Kaydediliyor...' : 'Tamamla ve Devam Et'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LetterDetail;
